import { useMemo, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, CalendarDays, LayoutGrid, Salad, Dumbbell } from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { CalendarMonthView } from '../components/CalendarMonthView';
import { CalendarWeekView } from '../components/CalendarWeekView';
import { AppointmentEditor, EditorMode } from '../components/AppointmentEditor';
import { useAppointments } from '../lib/queries';
import { useRole } from '../lib/role';
import { useAuth } from '../lib/auth';
import type { AppointmentWithPatient } from '../lib/api';

type ViewMode = 'month' | 'week';

type RoleFilter = 'all' | 'nutri' | 'coach';

// Italian month names long-form
const MONTH_NAMES_LONG = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

/**
 * Ritorna { from, to } per il mese che contiene `date`.
 * Range espanso ai bordi: include settimana parziale del mese precedente e successivo
 * (Lun=1 a Dom=7 ISO).
 */
function getMonthRange(date: Date): { from: string; to: string } {
  const y = date.getFullYear();
  const m = date.getMonth();
  // Primo giorno mese
  const firstOfMonth = new Date(Date.UTC(y, m, 1));
  // Lun=1 (ISO), Dom=7 → JS getUTCDay: 0=Dom, 1=Lun...
  let dow = firstOfMonth.getUTCDay();
  if (dow === 0) dow = 7;
  const offsetStart = dow - 1;            // giorni indietro per arrivare a Lun
  const start = new Date(firstOfMonth);
  start.setUTCDate(start.getUTCDate() - offsetStart);

  // Ultimo giorno mese
  const lastOfMonth = new Date(Date.UTC(y, m + 1, 0));
  let dowEnd = lastOfMonth.getUTCDay();
  if (dowEnd === 0) dowEnd = 7;
  const offsetEnd = 7 - dowEnd;
  const end = new Date(lastOfMonth);
  end.setUTCDate(end.getUTCDate() + offsetEnd);

  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
}

/**
 * Ritorna { from, to } per la settimana ISO che contiene `date`.
 */
function getWeekRange(date: Date): { from: string; to: string } {
  // Lun start ISO
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  let dow = d.getUTCDay();
  if (dow === 0) dow = 7;
  const start = new Date(d);
  start.setUTCDate(start.getUTCDate() - (dow - 1));
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
}

function formatMonthLabel(date: Date): string {
  return `${MONTH_NAMES_LONG[date.getMonth()]} ${date.getFullYear()}`;
}

function formatWeekLabel(date: Date): string {
  const { from, to } = getWeekRange(date);
  const f = new Date(`${from}T12:00:00Z`);
  const t = new Date(`${to}T12:00:00Z`);
  const sameMonth = f.getMonth() === t.getMonth();
  if (sameMonth) {
    return `${f.getDate()}–${t.getDate()} ${MONTH_NAMES_LONG[f.getMonth()]} ${f.getFullYear()}`;
  }
  return `${f.getDate()} ${MONTH_NAMES_LONG[f.getMonth()].slice(0, 3)} – ${t.getDate()} ${MONTH_NAMES_LONG[t.getMonth()].slice(0, 3)} ${t.getFullYear()}`;
}

export default function Calendario() {
  const { user } = useAuth();
  const { proKind } = useRole();
  const proKindIsBoth = proKind === 'both';
  const isCoach = proKind === 'coach';
  const userProKind: 'nutri' | 'coach' | 'both' = proKindIsBoth ? 'both' : (isCoach ? 'coach' : 'nutri');

  const [view, setView] = useState<ViewMode>('week');
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');

  // Editor modal state
  const [editorState, setEditorState] = useState<{
    open: boolean;
    mode: EditorMode;
    appointmentId: string | null;
    prefillStart?: string;
  }>({ open: false, mode: 'create', appointmentId: null });

  // Day overflow modal (lista appuntamenti del giorno se troppi nella cella)
  const [dayDetailIso, setDayDetailIso] = useState<string | null>(null);

  // Build range query params
  const range = useMemo(() => {
    return view === 'month' ? getMonthRange(anchorDate) : getWeekRange(anchorDate);
  }, [view, anchorDate]);

  const apptQuery = useAppointments({
    from: range.from,
    to: range.to,
    pro_role: roleFilter,
  });

  // Navigate prev/next
  const goPrev = useCallback(() => {
    const d = new Date(anchorDate);
    if (view === 'month') {
      d.setMonth(d.getMonth() - 1);
    } else {
      d.setDate(d.getDate() - 7);
    }
    setAnchorDate(d);
  }, [anchorDate, view]);

  const goNext = useCallback(() => {
    const d = new Date(anchorDate);
    if (view === 'month') {
      d.setMonth(d.getMonth() + 1);
    } else {
      d.setDate(d.getDate() + 7);
    }
    setAnchorDate(d);
  }, [anchorDate, view]);

  const goToday = useCallback(() => {
    setAnchorDate(new Date());
  }, []);

  // Open editor in create mode (optionally prefilled with start datetime)
  const openCreate = useCallback((prefillStartIso?: string) => {
    setEditorState({
      open: true,
      mode: 'create',
      appointmentId: null,
      prefillStart: prefillStartIso,
    });
  }, []);

  // Open editor in view/edit mode for existing appointment
  const openAppointment = useCallback((appt: AppointmentWithPatient) => {
    // Se canceled → view-only; altrimenti edit
    const mode: EditorMode = appt.status === 'canceled' ? 'view' : 'edit';
    setEditorState({
      open: true,
      mode,
      appointmentId: appt.id,
    });
  }, []);

  const closeEditor = useCallback(() => {
    setEditorState({ open: false, mode: 'create', appointmentId: null });
  }, []);

  const periodLabel = view === 'month' ? formatMonthLabel(anchorDate) : formatWeekLabel(anchorDate);

  const appointments = apptQuery.data?.appointments || [];

  if (!user || user.role !== 'dietologist') {
    return (
      <>
        <ProTopBar breadcrumb="Studio Pro · Agenda" title="Calendario" />
        <div style={{ padding: 48, color: 'var(--mute)' }}>
          Sezione disponibile solo per professionisti.
        </div>
      </>
    );
  }

  return (
    <>
      <ProTopBar
        breadcrumb="Studio Pro · Appuntamenti"
        title="Calendario"
        customCtaLabel="Nuovo appuntamento"
        onCta={() => openCreate()}
      />

      {/* Calendar toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 0 18px',
          flexWrap: 'wrap',
        }}
      >
        {/* View toggle */}
        <div
          role="tablist"
          aria-label="Vista calendario"
          style={{
            display: 'inline-flex',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 999,
            padding: 3,
          }}
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === 'month'}
            onClick={() => setView('month')}
            style={toggleBtnStyle(view === 'month')}
          >
            <LayoutGrid size={13} strokeWidth={2} /> Mese
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'week'}
            onClick={() => setView('week')}
            style={toggleBtnStyle(view === 'week')}
          >
            <CalendarDays size={13} strokeWidth={2} /> Settimana
          </button>
        </div>

        {/* Period nav */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <button
            type="button"
            onClick={goPrev}
            aria-label={view === 'month' ? 'Mese precedente' : 'Settimana precedente'}
            style={iconNavBtnStyle()}
          >
            <ChevronLeft size={16} strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={goToday}
            style={{
              ...pillBtnStyle(false),
              fontFamily: 'var(--mono)',
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              fontSize: 11,
            }}
          >
            Oggi
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label={view === 'month' ? 'Mese successivo' : 'Settimana successiva'}
            style={iconNavBtnStyle()}
          >
            <ChevronRight size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Period label */}
        <div
          style={{
            fontFamily: 'var(--display)',
            fontWeight: 700,
            fontSize: 18,
            color: 'var(--ink)',
            letterSpacing: -0.4,
            textTransform: 'capitalize',
          }}
        >
          {periodLabel}
        </div>

        <div style={{ flex: 1 }} />

        {/* Role filter (solo se pro_kind='both') */}
        {proKindIsBoth && (
          <div
            role="tablist"
            aria-label="Filtra per ruolo"
            style={{
              display: 'inline-flex',
              gap: 6,
            }}
          >
            <button
              type="button"
              role="tab"
              aria-selected={roleFilter === 'all'}
              onClick={() => setRoleFilter('all')}
              style={pillBtnStyle(roleFilter === 'all')}
            >
              Tutti
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={roleFilter === 'nutri'}
              onClick={() => setRoleFilter('nutri')}
              style={pillBtnStyle(roleFilter === 'nutri')}
            >
              <Salad size={12} strokeWidth={2} /> Nutri
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={roleFilter === 'coach'}
              onClick={() => setRoleFilter('coach')}
              style={pillBtnStyle(roleFilter === 'coach')}
            >
              <Dumbbell size={12} strokeWidth={2} /> Coach
            </button>
          </div>
        )}

        {/* Create button (also mirrored in topbar CTA for redundancy) */}
        <button
          type="button"
          onClick={() => openCreate()}
          style={{
            background: 'var(--ink)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '8px 14px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <Plus size={14} strokeWidth={2.4} />
          Nuovo
        </button>
      </div>

      {/* Body: loading/error/empty/data */}
      {apptQuery.isLoading && (
        <div style={loadingBoxStyle()}>Caricamento appuntamenti…</div>
      )}

      {apptQuery.isError && (
        <div style={errorBoxStyle()}>
          Errore caricamento appuntamenti.{' '}
          <button
            type="button"
            onClick={() => apptQuery.refetch()}
            style={{
              marginLeft: 8,
              background: 'transparent',
              border: '1px solid currentColor',
              borderRadius: 8,
              padding: '4px 10px',
              color: 'inherit',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Riprova
          </button>
        </div>
      )}

      {!apptQuery.isLoading && !apptQuery.isError && (
        <>
          {view === 'month' ? (
            <CalendarMonthView
              anchorDate={anchorDate}
              appointments={appointments}
              onDayClick={(iso) => setDayDetailIso(iso)}
              onAppointmentClick={openAppointment}
              onCreateOnDay={(iso) => openCreate(`${iso}T09:00`)}
            />
          ) : (
            <CalendarWeekView
              anchorDate={anchorDate}
              appointments={appointments}
              onAppointmentClick={openAppointment}
              onSlotClick={(isoLocal) => openCreate(isoLocal)}
            />
          )}
        </>
      )}

      {/* Day detail modal (month overflow) */}
      {dayDetailIso && (
        <DayDetailModal
          dayIso={dayDetailIso}
          appointments={appointments}
          onClose={() => setDayDetailIso(null)}
          onAppointmentClick={(a) => {
            setDayDetailIso(null);
            openAppointment(a);
          }}
          onCreate={() => {
            setDayDetailIso(null);
            openCreate(`${dayDetailIso}T09:00`);
          }}
        />
      )}

      {/* Editor modal */}
      {editorState.open && (
        <AppointmentEditor
          mode={editorState.mode}
          appointmentId={editorState.appointmentId}
          prefillStart={editorState.prefillStart}
          proKind={userProKind}
          activeRoleFilter={roleFilter}
          onClose={closeEditor}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Day detail modal — shown when user clicks a day with overflow appointments
// ---------------------------------------------------------------------------

function DayDetailModal({
  dayIso,
  appointments,
  onClose,
  onAppointmentClick,
  onCreate,
}: {
  dayIso: string;
  appointments: AppointmentWithPatient[];
  onClose: () => void;
  onAppointmentClick: (a: AppointmentWithPatient) => void;
  onCreate: () => void;
}) {
  const dayAppts = useMemo(
    () => appointments.filter((a) => a.start_at.slice(0, 10) === dayIso)
      .sort((a, b) => a.start_at.localeCompare(b.start_at)),
    [appointments, dayIso]
  );
  const date = new Date(`${dayIso}T12:00:00Z`);
  const label = new Intl.DateTimeFormat('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);

  return (
    <div style={modalOverlayStyle()} onClick={onClose} role="dialog" aria-modal>
      <div
        style={modalContentStyle(420)}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontFamily: 'var(--display)',
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--ink)',
              textTransform: 'capitalize',
            }}
          >
            {label}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 22,
              color: 'var(--mute)',
              cursor: 'pointer',
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: 16, maxHeight: 420, overflowY: 'auto' }}>
          {dayAppts.length === 0 ? (
            <div style={{ color: 'var(--mute)', fontSize: 14, padding: 12 }}>
              Nessun appuntamento.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dayAppts.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onAppointmentClick(a)}
                  style={dayApptRowStyle(a.status)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={dayApptDotStyle(a.status, a.pro_role)} />
                    <span
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'var(--ink-2)',
                        letterSpacing: 0.4,
                      }}
                    >
                      {formatHm(a.start_at)}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)', flex: 1, textAlign: 'left' }}>
                      {a.title || labelForType(a.type)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--mute)',
                      marginLeft: 24,
                      marginTop: 2,
                      textAlign: 'left',
                    }}
                  >
                    {a.patient_name}
                    {a.patient_human_id && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontFamily: 'var(--mono)',
                          fontSize: 11,
                          color: 'var(--mute-2)',
                        }}
                      >
                        {a.patient_human_id}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '12px 16px',
            borderTop: '1px solid var(--line)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button type="button" onClick={onClose} style={modalSecondaryBtnStyle()}>
            Chiudi
          </button>
          <button type="button" onClick={onCreate} style={modalPrimaryBtnStyle()}>
            <Plus size={14} strokeWidth={2.4} />
            Nuovo
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utility helpers per labels / formatters (riusati in più punti)
// ---------------------------------------------------------------------------

export const TYPE_LABELS: Record<string, string> = {
  visita: 'Visita',
  'follow-up': 'Follow-up',
  sessione: 'Sessione',
  misurazione: 'Misurazione',
  consulenza: 'Consulenza',
  altro: 'Altro',
};

function labelForType(t: string): string {
  return TYPE_LABELS[t] || t;
}

function formatHm(iso: string): string {
  return new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

// ---------------------------------------------------------------------------
// Inline style helpers — riusate in più sub-componenti per coerenza visiva.
// ---------------------------------------------------------------------------

function toggleBtnStyle(active: boolean): React.CSSProperties {
  return {
    background: active ? 'var(--ink)' : 'transparent',
    color: active ? '#fff' : 'var(--ink-3)',
    border: 'none',
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    cursor: 'pointer',
  };
}

function pillBtnStyle(active: boolean): React.CSSProperties {
  return {
    background: active ? 'var(--ink)' : 'var(--surface)',
    color: active ? '#fff' : 'var(--ink-3)',
    border: active ? '1px solid var(--ink)' : '1px solid var(--line)',
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    cursor: 'pointer',
  };
}

function iconNavBtnStyle(): React.CSSProperties {
  return {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 8,
    width: 30,
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--ink-3)',
    cursor: 'pointer',
  };
}

function loadingBoxStyle(): React.CSSProperties {
  return {
    padding: '48px 24px',
    textAlign: 'center',
    color: 'var(--mute)',
    background: 'var(--surface)',
    borderRadius: 14,
    border: '1px solid var(--line)',
  };
}

function errorBoxStyle(): React.CSSProperties {
  return {
    padding: 20,
    background: 'var(--orange-tint)',
    border: '1px solid var(--orange-soft)',
    borderRadius: 12,
    color: 'var(--orange-deep)',
    fontSize: 13,
    fontWeight: 500,
  };
}

function modalOverlayStyle(): React.CSSProperties {
  return {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(11, 15, 12, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 16,
  };
}

function modalContentStyle(maxWidth: number): React.CSSProperties {
  return {
    background: 'var(--surface)',
    borderRadius: 16,
    boxShadow: '0 20px 60px rgba(11, 15, 12, 0.2)',
    width: '100%',
    maxWidth,
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };
}

function modalPrimaryBtnStyle(): React.CSSProperties {
  return {
    background: 'var(--ink)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  };
}

function modalSecondaryBtnStyle(): React.CSSProperties {
  return {
    background: 'var(--surface)',
    color: 'var(--ink-3)',
    border: '1px solid var(--line)',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  };
}

function dayApptRowStyle(status: string): React.CSSProperties {
  const isCanceled = status === 'canceled';
  return {
    background: 'var(--surface-2)',
    border: '1px solid var(--line)',
    borderRadius: 10,
    padding: '10px 12px',
    cursor: 'pointer',
    textAlign: 'left',
    opacity: isCanceled ? 0.55 : 1,
    fontFamily: 'var(--sans)',
    display: 'block',
  };
}

function dayApptDotStyle(status: string, proRole: 'nutri' | 'coach'): React.CSSProperties {
  let bg = proRole === 'coach' ? 'var(--orange)' : 'var(--green)';
  if (status === 'completed') bg = 'var(--mute-2)';
  if (status === 'canceled') bg = 'var(--red)';
  if (status === 'no-show') bg = 'var(--yellow)';
  return {
    display: 'inline-block',
    width: 9,
    height: 9,
    borderRadius: '50%',
    background: bg,
    flexShrink: 0,
  };
}
